import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  ForkBranch,
  OnboardingV3ScriptID,
} from 'config/onboardingV3Steps';
import iamApi from 'store/api/iam';

export type OnboardingV3Stage =
  'conversation' | 'summary' | 'review' | 'finished';

export interface OnboardingV3Answer {
  optionID?: string;
  fieldValues?: Record<string, string>;
  texts?: string[];
}

export interface OnboardingV3State {
  /** Whose progress this is; another account signing in starts over. */
  accountID: string | null;
  stepIndex: number;
  scriptID: OnboardingV3ScriptID;
  /** How many messages came before the current conversation began. */
  threadStartIndex: number;
  branch: ForkBranch | null;
  isAiConsentGiven: boolean;
  answers: Record<string, OnboardingV3Answer>;
  isSubmitting: boolean;
  stage: OnboardingV3Stage;
  /** Set once the active session is loaded or created. */
  sessionId: string | null;
  /**
   * Handed-over documents the server has read, or given up on. Absent from
   * progress saved before it existed.
   */
  processedDocumentIds?: string[];
  /** Typed messages the server has read. Absent from older saved progress. */
  processedMessageIds?: string[];
}

const initialState: OnboardingV3State = {
  accountID: null,
  stepIndex: 0,
  scriptID: 'intake',
  threadStartIndex: 0,
  branch: null,
  isAiConsentGiven: false,
  answers: {},
  isSubmitting: false,
  stage: 'conversation',
  sessionId: null,
  processedDocumentIds: [],
  processedMessageIds: [],
};

export const onboardingV3Slice = createSlice({
  name: 'onboardingV3',
  initialState,
  reducers: {
    answerRecorded: (
      state,
      {
        payload,
      }: PayloadAction<{ stepID: string; answer: OnboardingV3Answer }>,
    ) => {
      state.answers[payload.stepID] = payload.answer;
    },

    textAppended: (
      state,
      { payload }: PayloadAction<{ stepID: string; text: string }>,
    ) => {
      const answer = state.answers[payload.stepID] ?? {};
      state.answers[payload.stepID] = {
        ...answer,
        texts: [...(answer.texts ?? []), payload.text],
      };
    },

    contextChanged: (
      state,
      {
        payload,
      }: PayloadAction<{
        branch?: ForkBranch;
        isAiConsentGiven?: boolean;
      }>,
    ) => {
      Object.assign(state, payload);
    },

    /** The server has finished with a handed-over document. */
    documentProcessed: (state, { payload }: PayloadAction<string>) => {
      const ids = (state.processedDocumentIds ??= []);
      if (!ids.includes(payload)) ids.push(payload);
    },

    /** The server has finished reading a typed message. */
    messageProcessed: (state, { payload }: PayloadAction<string>) => {
      const ids = (state.processedMessageIds ??= []);
      if (!ids.includes(payload)) ids.push(payload);
    },

    stepAdvanced: (
      state,
      {
        payload,
      }: PayloadAction<{ stepIndex: number; stage: OnboardingV3Stage }>,
    ) => {
      state.stepIndex = payload.stepIndex;
      state.stage = payload.stage;
    },

    conversationRestarted: (
      state,
      {
        payload,
      }: PayloadAction<{
        scriptID: OnboardingV3ScriptID;
        threadStartIndex: number;
      }>,
    ) => {
      state.scriptID = payload.scriptID;
      state.threadStartIndex = payload.threadStartIndex;
      state.stepIndex = 0;
      state.stage = 'conversation';
      state.isSubmitting = false;
    },

    /** The thread shows a fresh page from here; the script carries on. */
    pageTurned: (
      state,
      { payload }: PayloadAction<{ threadStartIndex: number }>,
    ) => {
      state.threadStartIndex = payload.threadStartIndex;
    },

    stageChanged: (state, { payload }: PayloadAction<OnboardingV3Stage>) => {
      state.stage = payload;
    },

    submittingChanged: (state, { payload }: PayloadAction<boolean>) => {
      state.isSubmitting = payload;
    },

    sessionLoaded: (
      state,
      {
        payload,
      }: PayloadAction<{
        sessionId: string;
        metadata: Record<string, unknown>;
      }>,
    ) => {
      state.sessionId = payload.sessionId;
      const m = payload.metadata;
      if (m.stepIndex !== undefined) state.stepIndex = m.stepIndex as number;
      if (m.scriptID !== undefined)
        state.scriptID = m.scriptID as OnboardingV3ScriptID;
      if (m.threadStartIndex !== undefined)
        state.threadStartIndex = m.threadStartIndex as number;
      if (m.branch !== undefined)
        state.branch = (m.branch as ForkBranch) ?? null;
      if (m.stage !== undefined) state.stage = m.stage as OnboardingV3Stage;
      if (m.isAiConsentGiven !== undefined)
        state.isAiConsentGiven = m.isAiConsentGiven as boolean;
      if (m.answers !== undefined)
        state.answers = m.answers as Record<string, OnboardingV3Answer>;
    },

    sessionCreated: (state, { payload }: PayloadAction<string>) => {
      state.sessionId = payload;
    },

    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      iamApi.endpoints.signInKinde.matchFulfilled,
      (state, { payload }) =>
        state.accountID === payload.id
          ? state
          : { ...initialState, accountID: payload.id },
    );
  },
});

export const {
  answerRecorded,
  textAppended,
  contextChanged,
  documentProcessed,
  messageProcessed,
  stepAdvanced,
  conversationRestarted,
  pageTurned,
  stageChanged,
  submittingChanged,
  sessionLoaded,
  sessionCreated,
  reset,
} = onboardingV3Slice.actions;

export const getOnboardingV3 = (state: {
  onboardingV3: OnboardingV3State;
}): OnboardingV3State => state.onboardingV3;

export default onboardingV3Slice.reducer;
